import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { FileDown, X, TrendingUp, TrendingDown } from 'lucide-react';

/* ─── Categorias de custo comercial (mesmo de CustosComercial) ─── */
const CUSTOS_COMERCIAL = [
  { key: 'custo_equipamento',       pagoKey: 'custo_equipamento_pago_em',       label: 'Equipamento' },
  { key: 'custo_instalacao',        pagoKey: 'custo_instalacao_pago_em',        label: 'Instalação' },
  { key: 'custo_indicacao',         pagoKey: 'custo_indicacao_pago_em',         label: 'Indicação' },
  { key: 'custo_logistica',         pagoKey: 'custo_logistica_pago_em',         label: 'Logística' },
  { key: 'custo_material_eletrico', pagoKey: 'custo_material_eletrico_pago_em', label: 'Mat. Elétrico' },
  { key: 'custo_crt',               pagoKey: 'custo_crt_pago_em',               label: 'CRT' },
  { key: 'custo_padrao',            pagoKey: 'custo_padrao_pago_em',            label: 'Padrão' },
  { key: 'custo_brinde',            pagoKey: 'custo_brinde_pago_em',            label: 'Brinde' },
];

const MONTHS_PT = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const fmt = (v) =>
  v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—';

/* Parseia "YYYY-MM-DD" sem shift de timezone */
const parseLocalDate = (str) => {
  if (!str) return null;
  const s = str.slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  return { year: y, month: m, day: d };
};

const formatDate = ({ year, month, day }) =>
  `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;

const S = {
  heading:   { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:       { fontSize: 13, color: '#475569', marginBottom: 24 },
  toolbar:   { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 20 },
  btnReport: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8,
               background: '#f6c90e', color: '#080c14', fontWeight: 700, fontSize: 13,
               cursor: 'pointer', border: 'none' },
  monthCard: { marginBottom: 24, background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, overflow: 'hidden' },
  monthHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                 padding: '12px 20px', background: '#0b111e', borderBottom: '1px solid #1a2035' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { padding: '8px 16px', fontSize: 11, color: '#475569', textTransform: 'uppercase',
               letterSpacing: 1, fontWeight: 700, borderBottom: '1px solid #1a2035', textAlign: 'left' },
  td:        { padding: '10px 16px', fontSize: 13, color: '#cbd5e1', borderBottom: '1px solid #0f1117' },
  totalRow:  { padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 32,
               alignItems: 'center', borderTop: '2px solid #1a2035', background: '#0b111e' },
  // Overlay / modal
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200,
               display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:     { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw' },
  mTitle:    { fontSize: 16, fontWeight: 700, color: '#f6c90e', marginBottom: 20 },
  label:     { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1,
               fontWeight: 700, display: 'block', marginBottom: 5 },
  input:     { width: '100%', background: '#080c14', border: '1px solid #1a2035', borderRadius: 7,
               padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none',
               boxSizing: 'border-box', marginBottom: 14 },
  select:    { width: '100%', background: '#080c14', border: '1px solid #1a2035', borderRadius: 7,
               padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none',
               boxSizing: 'border-box', marginBottom: 14, cursor: 'pointer' },
  mFooter:   { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  btnCancel: { padding: '8px 16px', borderRadius: 8, background: 'transparent',
               border: '1px solid #1a2035', color: '#64748b', fontSize: 13, cursor: 'pointer' },
  btnSave:   { padding: '8px 18px', borderRadius: 8, background: '#f6c90e',
               color: '#080c14', fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none' },
};

export default function ResumoFinanceiro() {
  const navigate = useNavigate();

  const [financeiros, setFinanceiros]       = useState([]);
  const [custosEmpresa, setCustosEmpresa]   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showModal, setShowModal]           = useState(false);
  const [reportTipo, setReportTipo]         = useState('mes'); // 'mes' | 'ano'
  const [reportMes, setReportMes]           = useState(new Date().getMonth() + 1);
  const [reportAno, setReportAno]           = useState(new Date().getFullYear());
  const [generating, setGenerating]         = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [finRes, custRes] = await Promise.all([
        api.get('/financeiro/'),
        api.get('/custos-contabil/'),
      ]);
      setFinanceiros(finRes.data);
      setCustosEmpresa(custRes.data);
    } catch {
      setFinanceiros([]);
      setCustosEmpresa([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Processar todas as linhas ─── */
  const allRows = useMemo(() => {
    const rows = [];

    for (const fin of financeiros) {
      // Entradas: cada pagamento registrado
      for (const pag of (fin.pagamentos || [])) {
        const parsed = parseLocalDate(pag.data_hora);
        if (!parsed) continue;
        rows.push({
          ...parsed,
          sortKey: `${parsed.year}-${String(parsed.month).padStart(2,'0')}-${String(parsed.day).padStart(2,'0')}`,
          descricao: `Pagamento — ${fin.cliente_nome}`,
          entrada: parseFloat(pag.valor),
          saida: null,
          link: `/financeiro?id=${fin.id}`,
          type: 'entrada',
        });
      }

      // Saídas comerciais: custos com pago_em preenchido
      for (const { key, pagoKey, label } of CUSTOS_COMERCIAL) {
        const valor  = fin[key];
        const pagoEm = fin[pagoKey];
        if (!valor || !pagoEm) continue;
        const parsed = parseLocalDate(pagoEm);
        if (!parsed) continue;
        rows.push({
          ...parsed,
          sortKey: `${parsed.year}-${String(parsed.month).padStart(2,'0')}-${String(parsed.day).padStart(2,'0')}`,
          descricao: `${label} — ${fin.cliente_nome}`,
          entrada: null,
          saida: parseFloat(valor),
          link: `/custos-comercial?id=${fin.id}`,
          type: 'custo_comercial',
        });
      }
    }

    // Saídas da empresa
    for (const c of custosEmpresa) {
      const parsed = parseLocalDate(c.data);
      if (!parsed) continue;
      rows.push({
        ...parsed,
        sortKey: `${parsed.year}-${String(parsed.month).padStart(2,'0')}-${String(parsed.day).padStart(2,'0')}`,
        descricao: c.descricao,
        entrada: null,
        saida: parseFloat(c.valor),
        link: '/custos-da-empresa',
        type: 'custo_empresa',
      });
    }

    rows.sort((a, b) => a.sortKey > b.sortKey ? 1 : -1);
    return rows;
  }, [financeiros, custosEmpresa]);

  /* ─── Agrupar por mês (decrescente) ─── */
  const monthGroups = useMemo(() => {
    const groups = {};
    for (const row of allRows) {
      const key = `${row.year}-${String(row.month).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = { year: row.year, month: row.month, rows: [] };
      groups[key].rows.push(row);
    }
    return Object.values(groups).sort((a, b) =>
      a.year !== b.year ? b.year - a.year : b.month - a.month
    );
  }, [allRows]);

  /* ─── Anos disponíveis para o seletor ─── */
  const availableYears = useMemo(() => {
    const years = new Set(allRows.map(r => r.year));
    years.add(new Date().getFullYear());
    return [...years].sort((a, b) => b - a);
  }, [allRows]);

  /* ─── A Pagar e A Receber (globais, exibidos no mês atual) ─── */
  const { aPagar, aReceber } = useMemo(() => {
    const aPagar = financeiros.reduce((s, fin) => {
      return s + CUSTOS_COMERCIAL.reduce((cs, { key, pagoKey }) => {
        const valor = parseFloat(fin[key]) || 0;
        return cs + (valor > 0 && !fin[pagoKey] ? valor : 0);
      }, 0);
    }, 0);

    const aReceber = financeiros.reduce((s, fin) => {
      const total    = parseFloat(fin.faturamento_total) || 0;
      const recebido = (fin.pagamentos || []).reduce((ps, p) => ps + (parseFloat(p.valor) || 0), 0);
      const pendente = total - recebido;
      return s + (pendente > 0 ? pendente : 0);
    }, 0);

    return { aPagar, aReceber };
  }, [financeiros]);

  /* ─── Gerar PDF via backend ─── */
  const handleGeneratePdf = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams({ ano: reportAno });
      if (reportTipo === 'mes') params.set('mes', reportMes);
      const resp = await api.get(`/financeiro/resumo-pdf/?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `resumo_financeiro_${reportAno}${reportTipo === 'mes' ? '_' + String(reportMes).padStart(2,'0') : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setShowModal(false);
    } catch (e) {
      alert('Erro ao gerar relatório: ' + (e.message || 'tente novamente'));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div style={S.heading}>Resumo Financeiro</div>
        <div style={{ color: '#475569', fontSize: 13, marginTop: 24 }}>Carregando...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={S.heading}>Resumo Financeiro</div>
      <div style={S.sub}>DRE simplificado — entradas e saídas por mês. Clique num valor para ver o detalhe.</div>

      <div style={S.toolbar}>
        <button style={S.btnReport} onClick={() => setShowModal(true)}>
          <FileDown size={15} /> Gerar Relatório PDF
        </button>
      </div>

      {monthGroups.length === 0 && (
        <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 40 }}>
          Nenhum lançamento financeiro registrado ainda.
        </div>
      )}

      {monthGroups.map(({ year, month, rows }) => {
        const totalEntrada = rows.reduce((s, r) => s + (r.entrada || 0), 0);
        const totalSaida   = rows.reduce((s, r) => s + (r.saida   || 0), 0);
        const lucro = totalEntrada - totalSaida;
        const isLucro = lucro >= 0;

        const now = new Date();
        const isCurrentMonth = year === now.getFullYear() && month === (now.getMonth() + 1);

        return (
          <div key={`${year}-${month}`} style={S.monthCard}>
            {/* Cabeçalho do mês */}
            <div style={S.monthHeader}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#f6c90e' }}>
                {MONTHS_PT[month]}/{year}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {isCurrentMonth && (
                  <>
                    {aPagar > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#f87171' }}>
                        A Pagar: {fmt(aPagar)}
                      </span>
                    )}
                    {aReceber > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#60a5fa' }}>
                        A Receber: {fmt(aReceber)}
                      </span>
                    )}
                    <div style={{ width: 1, height: 16, background: '#1a2035' }} />
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isLucro
                    ? <TrendingUp size={15} color="#4ade80" />
                    : <TrendingDown size={15} color="#f87171" />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: isLucro ? '#4ade80' : '#f87171' }}>
                    {isLucro ? 'Lucro:' : 'Prejuízo:'} {fmt(Math.abs(lucro))}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Data</th>
                    <th style={S.th}>Descrição</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Entrada</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Saída</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx}
                      onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(row.link)}>
                      <td style={{ ...S.td, color: '#94a3b8', fontSize: 12 }}>
                        {formatDate(row)}
                      </td>
                      <td style={S.td}>{row.descricao}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 600,
                                   color: row.entrada ? '#4ade80' : '#2d3748' }}>
                        {row.entrada ? fmt(row.entrada) : '—'}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 600,
                                   color: row.saida ? '#f87171' : '#2d3748' }}>
                        {row.saida ? fmt(row.saida) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total do mês */}
            <div style={S.totalRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>Total Entradas</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#4ade80' }}>{fmt(totalEntrada)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>Total Saídas</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#f87171' }}>{fmt(totalSaida)}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Modal Gerar Relatório */}
      {showModal && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={S.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={S.mTitle}>Gerar Relatório PDF</div>
              <button onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <label style={S.label}>Período</label>
            <select style={S.select} value={reportTipo} onChange={e => setReportTipo(e.target.value)}>
              <option value="mes">Mês específico</option>
              <option value="ano">Ano completo</option>
            </select>

            {reportTipo === 'mes' && (
              <>
                <label style={S.label}>Mês</label>
                <select style={S.select} value={reportMes} onChange={e => setReportMes(Number(e.target.value))}>
                  {MONTHS_PT.slice(1).map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
              </>
            )}

            <label style={S.label}>Ano</label>
            <select style={S.select} value={reportAno} onChange={e => setReportAno(Number(e.target.value))}>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <div style={S.mFooter}>
              <button style={S.btnCancel} onClick={() => setShowModal(false)}>Cancelar</button>
              <button style={S.btnSave} onClick={handleGeneratePdf} disabled={generating}>
                {generating ? 'Gerando...' : '📄 Gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
